import React, { useEffect, useRef, useState } from 'react';
import {
  FillVolumeUpRange,
  VolumeHoverDetector,
  VolumeRange,
  VolumeRangeWrapper,
  VolumeSectionContainer,
} from '../../Player/styles';
import { IconVolumeUp, IconVolumeOff, IconVolumeDown } from '../../../icons';
import theme from '../../../theme';
import { Icon } from '../../../styles';

const ControlBarVolumeController = ({ player }: { player: HTMLVideoElement }) => {
  const [isVolumeSectionHovered, setIsVolumeSectionHovered] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(() => (player?.muted ? 0 : player?.volume ?? 1));
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const IS_IOS = /Macintosh|iPad|iPhone|iPod/.test(navigator.userAgent);

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleMobileBreakpoint();
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read the level off the element rather than tracking it locally, so the icon shows the state
  // this control bar was mounted with — e.g. the mute carried over from an ad break.
  const handleVolume = () => {
    setVolume(player.muted ? 0 : player.volume);
  };

  useEffect(() => {
    if (!player) return;
    // No volume reset on mount: the element is muted here only when the browser refused to
    // autoplay it with sound, and lifting that with no gesture behind it is answered by pausing
    // the video. UnmuteNotice asks the viewer for the click instead.
    handleVolume();
    player.addEventListener('volumechange', handleVolume);

    return () => {
      player.removeEventListener('volumechange', handleVolume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleMobileBreakpoint();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
    setIsTablet(1250 > Number(window.innerWidth));
  };

  const handleChangeVolume = (volume) => {
    player.volume = volume;
    // Dragging the slider off/onto zero has to move `muted` with it, otherwise a player muted
    // from the icon stays silent while the slider claims otherwise.
    player.muted = volume === 0;
    setVolume(volume);
  };

  const toggleMute = () => {
    player.muted = !player.muted;
    player.volume = player.muted ? 0 : 1;
    handleVolume();
    // if (player.volume > 0) {
    //   player.volume = 0;
    //   setVolume(0);
    // } else if (player.volume === 0) {
    //   player.volume = 1;
    //   setVolume(1);
    // }
  };
  return (
    <VolumeSectionContainer
      onMouseEnter={() => setIsVolumeSectionHovered(true)}
      onMouseLeave={() => setIsVolumeSectionHovered(false)}
      style={{
        marginRight: isMobile ? '0.75re' : '1.5rem',
      }}
    >
      <Icon cursor="pointer" size="semiLarge" onClick={toggleMute}>{volume === 0 ? <IconVolumeOff /> : volume === 1 ? <IconVolumeUp />
        : <IconVolumeDown />}</Icon>
      {(isVolumeSectionHovered && !isMobile) && !(IS_IOS && isTablet) && <Volume value={volume} handleChange={handleChangeVolume} />}
    </VolumeSectionContainer>
  );
};

const Volume = ({ handleChange, value }) => {
  return (
    <VolumeRangeWrapper id="volumeBar">
      <VolumeRange
        value={value}
        type="range"
        min="0"
        max="1"
        step="0.1"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event.target.valueAsNumber)}
      />
      <FillVolumeUpRange volume={value} />
      <VolumeHoverDetector left={value} />
    </VolumeRangeWrapper>
  );
};

export default ControlBarVolumeController;
